import { Stack, Typography } from '@mui/material';
import { AWTypes, Genres, MappedGenres } from 'enums';
import { Scores, SidebarNote, SuggestionItem } from 'features/augmented-writing/augmented-writing.types';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import AnalysisDetails from '../../augmented-writing/analysis-results/analysis-details';
import MatchOverall from '../../augmented-writing/analysis-results/match-overall';
import UserDone from '../../augmented-writing/analysis-results/user-done';
import PaiButton from '../../ui/base-components/pai-button';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import PaiSwitch from '../../ui/base-components/pai-switch';

interface Props {
  genre: Genres | null;
  scores: Scores | undefined;
  allSuggestions: SuggestionItem[];
  sidebarNotes: SidebarNote[];
  userDone: boolean | undefined;
  contactAssessmentName: string | undefined;
  profileName: string | undefined;
  optimizationLoading: boolean;
  autoFormatLoading: boolean;
  textGenerationLoading: boolean;
  analysisLoading: boolean;
  clickedChartCategoryKey: string | undefined;
  suggestionsActive: boolean;
  hideTextGeneration: boolean;
  hideTextGenerationInputs: boolean;

  handleOpenProjectNameDialog(): void;

  onChartCategoryClicked(category: any): void;

  toggleSuggestionsActive(): void;
}

const TextGenerationAnalysisResults: React.FC<Props> = ({
  genre,
  scores,
  allSuggestions,
  sidebarNotes,
  userDone,
  contactAssessmentName,
  profileName,
  optimizationLoading,
  autoFormatLoading,
  textGenerationLoading,
  analysisLoading,
  clickedChartCategoryKey,
  suggestionsActive,
  hideTextGeneration,
  hideTextGenerationInputs,
  handleOpenProjectNameDialog,
  onChartCategoryClicked,
  toggleSuggestionsActive,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const textAnalysisLoading = optimizationLoading || autoFormatLoading || analysisLoading;
  const loading = textAnalysisLoading || textGenerationLoading;
  const domain: AWTypes = MappedGenres[genre || 'default'];

  return (
    <div className={classes.column}>
      {!loading && (!_.isEmpty(allSuggestions) || !_.isEmpty(sidebarNotes)) && (hideTextGeneration || hideTextGenerationInputs) ? (
        <Stack className={classes.toggle} direction={'row'} gap={2} justifyContent={'center'} alignItems={'center'}>
          <PaiSwitch checked={suggestionsActive} onChange={toggleSuggestionsActive} />
          <Typography className={cx(classes.text, suggestionsActive ? 'active' : null)}>{t('showSuggestions')}</Typography>
        </Stack>
      ) : null}
      <Stack className={classes.textGenerationResults} direction={'column'} gap={'8px'}>
        {!userDone ? <MatchOverall score={scores?.overall} /> : <UserDone contactAssessmentName={contactAssessmentName} profileName={profileName} />}
        <AnalysisDetails
          scores={scores}
          domain={domain}
          clickedChartCategoryKey={clickedChartCategoryKey}
          onChartCategoryClicked={onChartCategoryClicked}
        />
        {!_.isNil(genre) && (textGenerationLoading || textAnalysisLoading) ? (
          <PaiDotLoader
            text={
              textGenerationLoading
                ? t('generateGenreText', { genre: t(genre) })
                : analysisLoading
                ? t('generateAnalysisResults')
                : autoFormatLoading
                ? t('formatGenreText', { genre: t(genre) })
                : optimizationLoading
                ? t('optimizeGenreMatch', { genre: t(genre) })
                : undefined
            }
            gap={0.5}
            textSize={15}
          />
        ) : null}
        {!_.isNil(scores) && !textAnalysisLoading ? (
          <Stack gap={1} mt={'auto'}>
            <PaiButton text={genre === Genres.JOB_OFFER ? t('saveAndTitleSearch') : t('saveAndExit')} action={handleOpenProjectNameDialog} />
          </Stack>
        ) : null}
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  column: {
    position: 'relative',
    width: '280px',
    padding: '0 0 8px 0',
    border: 'none',
    [theme.breakpoints.down('xl')]: {
      height: '640px',
      width: '100%',
    },
    [theme.breakpoints.down('lg')]: {
      height: 'unset',
      width: '100%',
    },
  },
  textGenerationResults: {
    height: '100%',
    padding: '8px',
    overflow: 'auto',
  },
  toggle: {
    position: 'absolute',
    top: '-32px',
  },
  text: {
    fontSize: theme.typography.pxToRem(11),
    '&.active': {
      color: theme.palette.primary.main,
    },
  },
}));

export default TextGenerationAnalysisResults;
